import { FC, ReactNode } from 'react';
import { Drawer } from '@cfa/react-core';
import styled from 'styled-components';
import { themeSizes } from '../../styles/globalStyleVars';
import { DrawerHeader } from '../Equipment/Drawers/Troubleshooting/DrawerHeader';

interface SharedDrawerProps {
  open: boolean;
  onClose: () => void;
  headerName: string;
  children: ReactNode;
  noClickOutside?: boolean;
  disableBackdropClick?: boolean;
  width?: 'wide' | 'narrow';
  showHeader?: boolean;
  contentStyles?: React.CSSProperties;
  customHeader?: ReactNode;
  dataTestId?: string;
  zIndex?: number;
  overflowY?: 'auto' | 'hidden';
}

const DrawerContentWrapper = styled.div<{ $overflowY?: string }>`
  height: 100%;
  overflow-y: ${({ $overflowY }) => $overflowY ?? 'hidden'};
  display: flex;
  flex-direction: column;
  flex-grow: 1;
`;

// The @cfa/react-core Drawer uses a fixed pixel width (--width) with no
// responsive cap, so on viewports narrower than the tray the right-anchored
// drawer overflows off the left edge. Capping to the viewport keeps it fully
// visible on mobile while preserving the fixed width on larger screens.
const ResponsiveTray = styled(Drawer.Tray)`
  max-width: 100vw;
`;

export const SharedDrawer: FC<SharedDrawerProps> = ({
  open,
  onClose,
  headerName,
  children,
  disableBackdropClick = false,
  noClickOutside = false,
  width = 'wide',
  showHeader = true,
  contentStyles,
  customHeader,
  dataTestId,
  zIndex = 3000,
  overflowY = 'hidden',
}) => {
  return (
    <Drawer.Overlay
      isOpen={open}
      onOpenChange={isOpen => !isOpen && onClose()}
      isDismissable={!disableBackdropClick || !noClickOutside}
      style={{ zIndex }}
    >
      <ResponsiveTray
        size={width === 'wide' ? 'lg' : 'sm'}
        isCloseButtonHidden
        data-testid={dataTestId}
      >
        <DrawerContentWrapper $overflowY={overflowY} style={contentStyles}>
          {showHeader && !customHeader && (
            <DrawerHeader headerName={headerName} handleClose={onClose} />
          )}
          {customHeader}
          {children}
        </DrawerContentWrapper>
      </ResponsiveTray>
    </Drawer.Overlay>
  );
};

export const DrawerScrollBody = styled.div`
  flex: 1;
  overflow-y: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const DrawerContent = styled.div<{
  $isSmall?: boolean;
  $centered?: boolean;
}>`
  padding: ${themeSizes.xl};
  display: flex;
  flex-direction: column;
  ${({ $centered }) =>
    $centered &&
    'padding-top: 0; justify-content:center; align-items: center; flex-grow: 1'}
`;
